/**
 * Press outlets — the ONE canonical "As Featured" list (owner list, 07/20/2026).
 *
 * Each outlet renders as its official full-color logo from the `press-logos`
 * Supabase storage bucket (env-derived URL — never a hardcoded project host).
 * Logos are display-only: NOT links (owner ruling 07/20/2026).
 *
 * Shared by the sitewide footer band, homepage, /about-us, /press, and the
 * llms.txt media list so the surfaces can never drift. Logo sourcing record:
 * docs/Operations.md.
 */
export interface PressOutlet {
  name: string
  /** Object key inside the public `press-logos` storage bucket. */
  logo: string
}

/**
 * The date of the last full verification pass over this list (FRIDGE-601).
 * Rendered wherever the outlet count renders, so the claim always says how
 * current it is — a dated count can never "seem old", it either is current or
 * states exactly when it was last proven.
 *
 * The refresh process (run it, then move this date) is documented in
 * docs/Operations.md § "Press citations — evidence bank & refresh process".
 * Format MM/DD/YYYY (the house body-text date format).
 */
export const PRESS_OUTLETS_VERIFIED_AS_OF = '08/28/2026'

export const PRESS_OUTLETS: readonly PressOutlet[] = [
  { name: 'New York Post', logo: 'new-york-post.svg' },
  { name: 'Yahoo', logo: 'yahoo.svg' },
  { name: 'AOL', logo: 'aol.svg' },
  { name: 'Food & Wine', logo: 'food-and-wine.svg' },
  { name: 'Mirror', logo: 'mirror.svg' },
  { name: 'Express', logo: 'express.png' },
  { name: 'WikiHow', logo: 'wikihow.svg' },
  { name: 'Chowhound', logo: 'chowhound.svg' },
  { name: 'Daily Dot', logo: 'daily-dot.svg' },
  { name: 'First for Women', logo: 'first-for-women.png' },
  { name: 'Daily Record', logo: 'daily-record.svg' },
  { name: 'northjersey.com', logo: 'northjersey.svg' },
  { name: 'Rupa Health', logo: 'rupa-health.svg' },
  { name: 'Grokipedia', logo: 'grokipedia.png' },
  { name: 'The Fact Site', logo: 'the-fact-site.png' },
  { name: 'Daily Dish', logo: 'daily-dish.png' },
  { name: 'A Pinch of Healthy', logo: 'a-pinch-of-healthy.png' },
  { name: 'Cupcakes and Cutlery', logo: 'cupcakes-and-cutlery.png' },
  { name: 'Food Huggers', logo: 'food-huggers.png' },
  { name: 'UK.com', logo: 'uk-com.svg' },
  { name: 'Food&HotelAsia', logo: 'foodnhotelasia.png' },
  // Added 08/28/2026 (FRIDGE-601): live citing article verified at
  // thedailymeal.com/2219780/refrigerator-brand-avoid/ ("according to a
  // Fridge.com data analysis, 37.4% of U.S. households have at least two
  // working refrigerators"), and the site's largest press referrer in
  // PostHog since 07/20 (163 visits / 61 visitors). Evidence bank:
  // docs/Operations.md.
  { name: 'The Daily Meal', logo: 'the-daily-meal.svg' },
] as const

/**
 * Public URL for an outlet's logo. Returns the site-relative
 * fridge.com/press-logos/* proxy path (next.config rewrite → Supabase Storage)
 * so the rendered <img> only ever shows a fridge.com host, never a
 * *.supabase.co storage host (owner directive 07/20). Same-origin, so it
 * resolves against localhost in dev and fridge.com in prod.
 */
export function pressLogoUrl(outlet: PressOutlet): string {
  return `/press-logos/${outlet.logo}`
}

/**
 * Back-compat alias: earlier chrome code imported VERIFIED_PRESS_OUTLETS
 * (name+url tiles). The band is now logo-only; the alias keeps the shared
 * single-list contract for any straggler import.
 */
export const VERIFIED_PRESS_OUTLETS = PRESS_OUTLETS
